import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";

type EntityType = { id: number; name: string; unit: string };

const UNIT_OPTIONS = ["HA", "MT/hr", "count"];

export const EntityTypesConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [types, setTypes] = useState<EntityType[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ["entity_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("entity_types")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as EntityType[];
    },
  });

  useEffect(() => {
    if (data) setTypes(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const t of types) {
        if (t.id > 0) {
          const { error } = await supabase
            .from("entity_types")
            .update({ name: t.name, unit: t.unit })
            .eq("id", t.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("entity_types")
            .insert({ name: t.name, unit: t.unit });
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      notify("Entity types saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["entity_types"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleAdd = () => {
    setTypes((prev) => [
      ...prev,
      { id: -Date.now(), name: "", unit: "HA" },
    ]);
    setDirty(true);
  };

  const handleDelete = async (index: number) => {
    const t = types[index];
    if (t.id > 0) {
      const { error } = await supabase
        .from("entity_types")
        .delete()
        .eq("id", t.id);
      if (error) {
        notify(
          "Cannot delete — entity type may have linked entities or pricing",
          { type: "error" },
        );
        return;
      }
    }
    setTypes((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    queryClient.invalidateQueries({ queryKey: ["entity_types"] });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Manage entity types (Estate, Mill, Seed Garden, etc.). Each type
        has a sizing unit used for pricing brackets and deal value
        calculation.
      </p>

      <div className="space-y-2">
        {types.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2">
            <input
              type="text"
              value={t.name}
              onChange={(e) => {
                setTypes((prev) =>
                  prev.map((et, idx) =>
                    idx === i ? { ...et, name: e.target.value } : et,
                  ),
                );
                setDirty(true);
              }}
              placeholder="Entity type name"
              className="flex-1 px-3 py-1.5 text-sm border rounded-md"
            />
            <select
              value={t.unit}
              onChange={(e) => {
                setTypes((prev) =>
                  prev.map((et, idx) =>
                    idx === i ? { ...et, unit: e.target.value } : et,
                  ),
                );
                setDirty(true);
              }}
              className="h-8 w-24 rounded-md border bg-background px-2 text-sm"
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(i)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-1" />
          Add Entity Type
        </Button>
        {dirty && (
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
